import type { OfferView } from '@xchange/shared';
import type { ActivityItem, PairView } from './api.js';

/**
 * The book, read the same way by both trading screens: the browser's and the
 * one inside Telegram. Pure, so a change to the order of a side, to what
 * counts as finished, or to how a trade's price is worked out lands on both
 * screens at once and can be tested on its own.
 */

/** Done with: nothing of this order is on the book any more. */
export const finished = (status: string): boolean =>
  status === 'done' || status === 'cancelled' || status === 'expired' || status === 'failed';

/** Offers to sell this pair, lowest price first: the price a buyer pays. */
export function asksOf(open: readonly OfferView[], pairName: string | undefined): OfferView[] {
  return open
    .filter((o) => o.pair === pairName && o.side === 'sell')
    .sort((a, b) => (BigInt(a.price) < BigInt(b.price) ? -1 : 1));
}

/** Bids to buy this pair, highest price first: the price a seller gets. */
export function bidsOf(open: readonly OfferView[], pairName: string | undefined): OfferView[] {
  return open
    .filter((o) => o.pair === pairName && o.side === 'buy')
    .sort((a, b) => (BigInt(a.price) > BigInt(b.price) ? -1 : 1));
}

/** The gap between the best of each side, in the pair's price unit; null with a side empty. */
export function spreadOf(asks: readonly OfferView[], bids: readonly OfferView[]): bigint | null {
  const ask = asks[0];
  const bid = bids[0];
  return ask === undefined || bid === undefined ? null : BigInt(ask.price) - BigInt(bid.price);
}

/** The latest trades on this pair, newest first, at most `limit` of them. */
export function tradesOf(
  activity: readonly ActivityItem[],
  pairName: string | undefined,
  limit: number,
): ActivityItem[] {
  return activity.filter((a) => a.kind === 'trade' && a.pair === pairName).slice(0, limit);
}

/** The price a trade happened at, in the pair's own unit. */
export function tradePrice(
  pair: Pick<PairView, 'priceUnit'>,
  trade: { baseAmount: string; quoteAmount: string },
): bigint {
  const base = BigInt(trade.baseAmount);
  return base === 0n ? 0n : (BigInt(trade.quoteAmount) * BigInt(pair.priceUnit)) / base;
}
